// The joint-driven face of a MetaHuman GLB: ARKit-52 -> RigLogic -> face bones.
//
// A MetaHuman published by the GASS plugin has no ARKit blendshapes (measured:
// 0 morph targets on Aoi's export), so HeadModel's morph path moves nothing when
// she talks. Her face IS a rig, though — 865 FACIAL_* joints skinned into the
// face mesh — and the DNA that drives it in Unreal is the same DNA this repo
// already evaluates for splat heads through OpenRigLogic (src/lib/orl). So the
// drive is the existing chain with a different last step: the ARKit frame the
// face graph produces -> arkitToMh (188 MetaHuman controls) -> the ORL rig's
// evaluateRig -> one local delta per joint -> faceJointDrive poses the bones.
// Skinning does the rest on the GPU, as it does for the body.
//
// The rig arrives as the character's `orl_pack.bin` in its /ogs collection — the
// same asset a splat character carries, put there by the content browser's DNA
// drop (OrlDnaDirectUpload) or, later, by the plugin. `loadFaceRigForCharacter`
// looks for it on EVERY character: a GLB with the file gets a driven face, one
// without stays exactly as before, and a failure is reported on the seam and the
// console, never swallowed into a still face nobody can explain.
//
// Per frame it allocates nothing: one 188-float scratch, RigLogic's zero-copy
// output view, and the bone writes. Runs from HeadModel.useFrame AFTER the body
// mixer has posed the skeleton, so the five body joints the DNA also carries
// (spine_04..head) keep the mixer's pose — faceJointDrive binds FACIAL_ only.
import { createOrlRig } from './orl/orlRig.js'
import { orlAssetName, ORL_PACK_FILE } from './orl/bundleFiles.js'
import { arkitToMh, MH_LEN } from '../ogs/rig/arkitToMh.js'
import { MH_RIG_NAMES } from '../ogs/rig/rigNamesMh.js'
import { bindFaceJoints, applyJointPoses, restoreRest } from './faceJointDrive.js'
import { listOgsAssets, cachedFetchAamFile, uploadOgsAsset } from './assetManagerApi.js'
import { pickFaceRigSource, FACE_DNA_ASSET } from './faceRigSource.js'
import { packAsUploadFile } from './orl/dnaUpload.js'
import { convertDnaInWorker } from './orl/dnaToPackWorker.js'

/** The /ogs asset name the pack is stored under (`orl_pack.bin`). */
export const ORL_PACK_ASSET = orlAssetName(ORL_PACK_FILE)

/**
 * At least this many of the 188 MetaHuman controls must map onto the DNA's GUI
 * controls. Aoi maps 174 (the 14 short are eye-aim widgets her DNA lacks); a
 * DNA authored in another control layout maps a handful and would render a
 * face that moves the wrong parts with plausible values — refuse it instead.
 */
export const MIN_CONTROLS_MAPPED = 100

/**
 * Module singleton, read by HeadModel every frame and exposed as
 * `window.__FACE_RIG_DRIVE__`. Not React state: the render loop reads it.
 */
export const faceRigDrive = {
  /** 'idle' | 'loading' | 'active' | 'none' | 'error' */
  state: 'idle',
  /** True while a driver is bound and should be fed frames. */
  active: false,
  /** Which character the current state describes. */
  slug: null,
  /** Where the rig came from: 'pack' (orl_pack.bin) | 'dna' (head.dna baked on this load) | null. */
  source: null,
  /** Load / bind report: joints bound, DNA joints missing on the rig, controls mapped. */
  report: null,
  error: null,
  /** Frames driven since bind. */
  frames: 0,
  /** Who owned the last frame (faceFrame.js): forced | rig | live | clips | idle. */
  owner: 'idle',
  /** The last ARKit frame driven, for the seam (a copy, not the live scratch). */
  lastArkit: new Float32Array(52),
  _driver: null,
  /** Feed one ARKit-52 frame. No-op unless active. */
  drive(arkit) {
    const d = this._driver
    if (!d) return
    d.drive(arkit)
    this.lastArkit.set(arkit)
    this.frames++
  },
  /**
   * Console/test seam: drive one explicit frame (an array of 52) right now,
   * outside the render loop. The next rendered frame overwrites it, so pair it
   * with `__FORCE_ARKIT__` to hold a pose.
   */
  driveArkit(arr) {
    if (!this._driver) throw new Error(`[faceRig] no driver bound (state: ${this.state}${this.error ? ', ' + this.error : ''})`)
    this.drive(arr)
    return this.report
  },
}

if (typeof window !== 'undefined') window.__FACE_RIG_DRIVE__ = faceRigDrive

/**
 * Builds a driver from a pack and a skeleton.
 * @param {object} opts
 * @param {Uint8Array} opts.packBytes  the character's orl_pack.bin
 * @param {Map<string, object>} opts.bones  bone name -> THREE.Bone
 * @param {(m:string)=>void} [opts.log]
 * @returns {Promise<{ drive:(arkit:ArrayLike<number>)=>void, dispose:()=>void, report:object }>}
 */
export async function createFaceRigDriver({ packBytes, bones, log = () => {} }) {
  const rig = await createOrlRig({
    getBytes: (name) => (name === ORL_PACK_ASSET ? packBytes : undefined),
    controlNames: MH_RIG_NAMES,
    minMatched: MIN_CONTROLS_MAPPED,
    log,
  })
  const jointNames = rig.manifest?.jointNames
  if (!Array.isArray(jointNames) || jointNames.length === 0) {
    rig.release()
    throw new Error('[faceRig] this orl_pack.bin carries no jointNames — re-bake it with a riglogic.wasm that exports getJointName')
  }
  const binding = bindFaceJoints(jointNames, bones)
  if (binding.count === 0) {
    rig.release()
    throw new Error(`[faceRig] none of the DNA's ${jointNames.length} joints exist on this rig — the pack belongs to another character`)
  }
  const mh = new Float32Array(MH_LEN)
  const report = {
    joints: binding.count,
    dnaJoints: jointNames.length,
    missing: binding.missing,
    bodyJoints: binding.bodyJoints,
    controlsMapped: rig.matched,
    controlsTotal: MH_RIG_NAMES.length,
    unmappedControls: rig.unmatched,
  }
  return {
    report,
    drive(arkit) {
      arkitToMh(arkit, mh)
      const { jointOut } = rig.evaluateRig(mh)
      applyJointPoses(binding, jointOut)
    },
    dispose() {
      restoreRest(binding)
      rig.release()
    },
  }
}

/** Every bone under `root`, by name. */
export function collectBones(root) {
  const bones = new Map()
  root.traverse((o) => { if (o.isBone && o.name && !bones.has(o.name)) bones.set(o.name, o) })
  return bones
}

let _loadToken = 0

/**
 * The pack from the character's own head.dna, baked here and written back.
 *
 * The GASS plugin publishes a MetaHuman with its DNA in the bundle (head.dna)
 * because the bake — dnaToPack, riglogic wasm — is a browser-side conversion it
 * cannot run. So the first load of such a character bakes the pack (off the UI
 * thread, tens of seconds on a 75 MB DNA) and uploads it as orl_pack.bin, so
 * every later load, by anyone, finds the pack directly. The write-back failing
 * is reported and does NOT stop the face: the pack is in hand, the drive runs,
 * and the next load bakes again.
 */
async function bakePackFromDna(slug, row, { log }) {
  const url = row.fileUrl ?? row.file_url
  const res = await cachedFetchAamFile(url, row.size)
  if (!res.ok) throw new Error(`${FACE_DNA_ASSET}: HTTP ${res.status}`)
  const dnaBytes = new Uint8Array(await res.arrayBuffer())
  log(`[faceRig] ${slug}: no orl_pack.bin yet; baking one from ${FACE_DNA_ASSET} (${(dnaBytes.length / 1048576).toFixed(1)} MB)`)
  const packBytes = await convertDnaInWorker(dnaBytes, { onLog: (m) => log(m) })
  try {
    const fd = new FormData()
    const file = packAsUploadFile(packBytes)
    fd.append('file', file, file.name)
    await uploadOgsAsset(slug, fd)
    log(`[faceRig] ${slug}: ${file.name} written back to the bundle (${(packBytes.length / 1048576).toFixed(1)} MB); later loads skip the bake`)
  } catch (e) {
    console.error(`[faceRig] ${slug}: the baked pack could not be written back — the face drives from memory this session and the next load bakes again:`, e)
  }
  return packBytes
}

/**
 * Looks for the character's orl_pack.bin — or the head.dna the GASS plugin
 * publishes, baked into one on first load (faceRigSource.js) — and, when
 * present, binds a driver to the rig under `root`. Resolves when the state is
 * settled; never throws — the outcome is on `faceRigDrive` (state/report/error)
 * and the console.
 *
 * Racing loads resolve to the LAST call: a character switch mid-download
 * disposes the driver that arrives late instead of driving the wrong rig.
 */
export async function loadFaceRigForCharacter(slug, root, { log = (m) => console.info(m) } = {}) {
  const token = ++_loadToken
  disposeFaceRig()
  faceRigDrive.slug = slug
  faceRigDrive.source = null
  faceRigDrive.state = 'loading'
  faceRigDrive.error = null
  faceRigDrive.report = null
  try {
    const rows = await listOgsAssets(slug)
    const source = pickFaceRigSource(rows)
    if (token !== _loadToken) return null
    if (source.kind === 'none') {
      faceRigDrive.state = 'none'
      return null
    }
    faceRigDrive.source = source.kind
    let packBytes
    if (source.kind === 'dna') {
      packBytes = await bakePackFromDna(slug, source.row, { log })
    } else {
      const url = source.row.fileUrl ?? source.row.file_url
      const res = await cachedFetchAamFile(url, source.row.size)
      if (!res.ok) throw new Error(`${ORL_PACK_ASSET}: HTTP ${res.status}`)
      packBytes = new Uint8Array(await res.arrayBuffer())
    }
    if (token !== _loadToken) return null
    const driver = await createFaceRigDriver({ packBytes, bones: collectBones(root), log })
    if (token !== _loadToken) { driver.dispose(); return null }
    faceRigDrive._driver = driver
    faceRigDrive.report = driver.report
    faceRigDrive.frames = 0
    faceRigDrive.state = 'active'
    faceRigDrive.active = true
    const r = driver.report
    log(`[faceRig] ${slug}: RigLogic drives ${r.joints} of ${r.dnaJoints} DNA joints (${r.missing.length} missing on the rig, ` +
      `${r.bodyJoints.length} left to the body mixer); ${r.controlsMapped}/${r.controlsTotal} controls mapped`)
    return driver
  } catch (e) {
    if (token !== _loadToken) return null
    faceRigDrive.state = 'error'
    faceRigDrive.error = e?.message ?? String(e)
    console.error(`[faceRig] ${slug}: the face rig could not be bound — the face stays still:`, e)
    return null
  }
}

/** Puts the bones back at rest and drops the rig. Safe to call with nothing bound. */
export function disposeFaceRig() {
  const d = faceRigDrive._driver
  faceRigDrive._driver = null
  faceRigDrive.active = false
  faceRigDrive.owner = 'idle'
  if (d) {
    try { d.dispose() } catch (e) { console.warn('[faceRig] dispose failed:', e) }
  }
  if (faceRigDrive.state !== 'loading') faceRigDrive.state = 'idle'
}
